import { FC, useEffect, useState } from 'react';
import { SCUIContent } from '../../atomic/organisms';
import {
    Box,
    Button,
    Grid,
    Tooltip,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
} from '@mui/material';
import { SCUIBreadCrumb, SCUISelectFieldWithButton } from '../../atomic/molecules';
import { t } from 'i18next';
import { PostCheckoutQuery, DeliveryQuery } from '../../services/query';
import { PostCheckoutCommand } from '../../services/command';
import Swal from 'sweetalert2';
import TimeHelper from '../../utils/time';
import { SCUILoading } from '../../atomic/atoms';
import { NoResultFoundComponent } from '../../pages/CustomerManagement/components/NoResultFoundComponent/NoResultFoundComponent';
import Helper from '@/utils/helper';
import SCUIDownloadButton from '@/atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';

export const PendingShipmentsPage: FC<any> = () => {
    const [cargoNameList, setCargoNameList] = useState<any>();
    const [selectedCargo, setSelectedCargo] = useState(0);
    const pageSize = 100;
    const pageNumber = 0;
    const [datas, setDatas] = useState<any>([]);
    const [storeShippingCompanyMappings, setStoreShippingCompanyMappings] = useState<any[]>([]);
    const [selectedStoreCode, setSelectedStoreCode] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [selectedRowsForDownload, setSelectedForDownload] = useState<any[]>([]);
    const [isExcelDownloaded, setIsExcelDownloaded] = useState<boolean>(false);
    const [show, setShow] = useState(0);
    const [excelLogo, setExcelLogo] = useState<string | null>(null);
    const [mappedStoreShippingCompanyId, setMappedStoreShippingCompanyId] = useState<number | null>(null);
    const [cancelled, setCancelled] = useState(false);

    const selectedCountry = JSON.parse(localStorage.getItem('selectedCountry') || '{}');
    const countryId: number = Number(selectedCountry?.countryId) || 0;

    const requiresStoreSelection = (cargoId: number): boolean =>
        cargoId > 0 && mappedStoreShippingCompanyId != null && cargoId === mappedStoreShippingCompanyId;

    const resetGridResults = () => {
        setDatas([]);
        setShow(0);
        setSelectedRows([]);
        setSelectedForDownload([]);
        setIsExcelDownloaded(false);
    };

    const RequestPickupForShipments = () => {
        setIsLoading(true);
        let params: any = {};
        params.ShippingCompanyId = selectedCargo;
        params.Shipments = selectedRows.map((item: any) => item.shipmentNumber);
        PostCheckoutCommand.RequestPickupForShipments(params)
            .then((res: any) => {
                if (res.isSuccess) {
                    Swal.fire(t('general.success'), '', 'success');
                } else {
                    Swal.fire(t('general.error'), '', 'error');
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            })
            .finally(() => {
                setIsLoading(false);
                setSelectedRows([]);
                getGridData(pageNumber, pageSize, selectedCargo);
            });
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const allRows = datas?.data || [];
            setSelectedRows(allRows);
            setSelectedForDownload(
                allRows.map((item: any, index: number) => ({
                    ['no']: index + 1,
                    ['shipmentNumber']: item.shipmentNumber,
                    ['orderNumber']: item.orderNumber,
                    ['shippingCompanyName']: item.cargoCompanyName,
                    ['shipmentPackageCount']: item.shipmentPackageCount,
                    ['shipmentDate']: TimeHelper.formatDateHour(item.shipmentDate),
                    ['grandTotal']: item.grandTotal,
                }))
            );
        } else {
            setSelectedRows([]);
            setSelectedForDownload([]);
        }
        if (isExcelDownloaded) {
            setIsExcelDownloaded(false);
        }
    };

    const handleSelectRow = (row: any) => {
        const isSelected = selectedRows.some((selectedRow) => selectedRow.orderId === row.orderId);
        let newSelectedRows: any[];

        if (isSelected) {
            newSelectedRows = selectedRows.filter((selectedRow) => selectedRow.orderId !== row.orderId);
        } else {
            newSelectedRows = [...selectedRows, row];
        }

        setSelectedRows(newSelectedRows);
        setSelectedForDownload(
            newSelectedRows.map((item: any, index: number) => ({
                ['no']: index + 1,
                ['shipmentNumber']: item.shipmentNumber,
                ['orderNumber']: item.orderNumber,
                ['shippingCompanyName']: item.cargoCompanyName,
                ['shipmentPackageCount']: item.shipmentPackageCount,
                ['shipmentDate']: TimeHelper.formatDateHour(item.shipmentDate),
                ['grandTotal']: item.grandTotal,
            }))
        );

        if (isExcelDownloaded) {
            setIsExcelDownloaded(false);
        }
    };

    const isRowSelected = (row: any) => {
        return selectedRows.some((selectedRow) => selectedRow.orderId === row.orderId);
    };

    const GetCargoList = async () => {
        try {
            let cargoList: any = await PostCheckoutQuery.GetShippingCompanies();
            if (cargoList.shippingCompanies) {
                setCargoNameList(cargoList.shippingCompanies);
            }
        } catch (error) {
            console.log('Hata :>> ', error);
        }
    };

    useEffect(() => {
        GetCargoList();
    }, []);

    useEffect(() => {
        if (cancelled) {
            return;
        }
        if (!countryId) {
            setMappedStoreShippingCompanyId(null);
            return;
        }
        DeliveryQuery.GetCargoStoreShippingCompanyMapping({ countryId }).then((res: any) => {
            const numId = Number(res?.data?.shippingCompanyId);
            setMappedStoreShippingCompanyId(Number.isFinite(numId) && numId > 0 ? numId : null);
        });
        return () => {
            setCancelled(true);
        };
    }, [countryId, cancelled]);

    const columns = [
        {
            field: 'shipmentNumber',
            headerName: t('general.shippingNo'),
        },
        {
            field: 'orderNumber',
            headerName: t('general.orderNo'),
        },
        {
            field: 'shipmentDate',
            headerName: t('general.shippedDate'),
        },
        {
            field: 'grandTotal',
            headerName: t('general.cost'),
        },
    ];
    const getGridData = (currentPageSize: number, currentPage: number, cargoCompanyId: any) => {
        setIsLoading(true);
        if (selectedCargo !== 0) {
            const baseParams: any = {
                take: 10000,
                skip: 0,
                cargoCompanyId: cargoCompanyId,
            };
            if (selectedStoreCode) {
                baseParams.storeCode = selectedStoreCode;
            }
            PostCheckoutCommand.GetWaitingForShipments(baseParams)
                .then((res: any) => {
                    if (res) {
                        setDatas(res);
                    }
                    if (selectedCargo > 0) {
                        setShow(1);
                    }
                    setIsLoading(false);
                })
                .catch((res: any) => {
                    Swal.fire(t(res.error) || t('general.failed'), '', 'error');
                    setIsLoading(false);
                });
        } else {
            Swal.fire(t('general.cargoCompanyRequiredMessage'), '', 'error');
            setIsLoading(false);
        }
    };
    const GetStoreShippingCompanyMappings = () => {
        DeliveryQuery.GetStoreShippingCompanyMappings({ countryId: countryId, shippingCompanyId: selectedCargo })
            .then((res: any) => {
                setStoreShippingCompanyMappings(res?.data?.storeShippingCompanyMappings ?? res);
            })
            .catch((res: any) => {
                Swal.fire(t(res?.error) || t('general.failed'), '', 'error');
            })
            .finally(() => setIsLoading(false));
    };

    const handleDownload = () => {
        setIsExcelDownloaded(true);
        return selectedRowsForDownload;
    };

    const loadLogo = async () => {
        const logo = await Helper.getLcwLogo();
        if (logo) {
            setExcelLogo(logo);
        }
    };

    useEffect(() => {
        loadLogo();
    }, []);

    useEffect(() => {
        if (!requiresStoreSelection(selectedCargo)) {
            setStoreShippingCompanyMappings([]);
            return;
        }
        GetStoreShippingCompanyMappings();
    }, [countryId, selectedCargo, mappedStoreShippingCompanyId]);

    useEffect(() => {
        if (selectedCargo > 0 && show === 1) {
            getGridData(pageNumber, pageSize, selectedCargo);
        }
    }, [pageNumber, pageSize]);

    return (
        <SCUIContent>
            <SCUIBreadCrumb title={t('general.pendingShipments')} componentLink={'PendingShipmentsPage'} />
            <Grid
                container
                size={{ xs: 12, md: 12, lg: 12 }}
                alignItems={'center'}
                spacing={4}
                sx={{ marginTop: '2rem' }}
            >
                <Grid size={{ xs: 12, md: 4 }}>
                    <SCUISelectFieldWithButton
                        selectPlaceHolder={t('cargoFeeManagement.selectCargoCompany')}
                        selectValue={selectedCargo}
                        selectId={'shippingCompanyId'}
                        selectText={'name'}
                        onChangeSelect={(event: any) => {
                            setSelectedCargo(event.target.value as number);
                            setSelectedStoreCode('');
                            resetGridResults();
                        }}
                        selectList={cargoNameList}
                        onClickButton={() => {
                            if (requiresStoreSelection(selectedCargo) && !selectedStoreCode) {
                                Swal.fire(t('general.pleaseSelectStoreCode'), '', 'error');
                                return;
                            }
                            getGridData(pageNumber, pageSize, selectedCargo);
                        }}
                        onKeyPressTextField={(e: any) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (requiresStoreSelection(selectedCargo) && !selectedStoreCode) {
                                    Swal.fire(t('general.pleaseSelectStoreCode'), '', 'error');
                                    return;
                                }
                                getGridData(pageNumber, pageSize, selectedCargo);
                            }
                        }}
                        buttonText={t('general.list')}
                    />
                </Grid>
                {requiresStoreSelection(selectedCargo) && (
                    <Grid size={{ xs: 12, md: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Select
                                displayEmpty
                                value={selectedStoreCode}
                                onChange={(e: any) => {
                                    setSelectedStoreCode(e.target.value);
                                    resetGridResults();
                                }}
                                renderValue={(selected) => (selected ? selected : String(t('general.selectStoreCode')))}
                            >
                                {storeShippingCompanyMappings?.map((item: any, idx: number) => (
                                    <MenuItem key={idx} value={item.storeCode}>
                                        {item.storeCode}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Grid>
                )}
            </Grid>
            {datas?.data?.length > 0 ? (
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ marginTop: '1.5rem', marginBottom: '1rem' }}
                    >
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 30,
                                    backgroundColor: 'lightgray',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 2,
                                    fontSize: 14,
                                    fontWeight: 'bold',
                                }}
                            >
                                {t('excel.orderQuantity')} : {datas?.count}
                            </Box>
                        </Grid>
                        {selectedRows?.length > 0 && (
                            <Grid size={{ xs: 12, sm: 6, md: 'auto' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 1,
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <SCUIDownloadButton
                                            position="right"
                                            containerStyle={{ marginLeft: '16px', height: '40px' }}
                                            excelData={{
                                                excelDownloadData: selectedRowsForDownload,
                                                excelDownloadFolderName: t('general.pendingShipments'),
                                                logo: excelLogo || undefined,
                                            }}
                                            onclick={() => handleDownload()}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            color="info"
                                            disabled={!isExcelDownloaded}
                                            sx={{ height: 40, width: '250px' }}
                                            onClick={() => RequestPickupForShipments()}
                                        >
                                            {String(t('general.sendDeliveryListByEmail'))}
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={
                                                selectedRows.length > 0 &&
                                                selectedRows.length < (datas?.data || []).length
                                            }
                                            checked={
                                                (datas?.data || []).length > 0 &&
                                                selectedRows.length === (datas?.data || []).length
                                            }
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    {columns.map((column) => (
                                        <TableCell key={column.field}>{column.headerName}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(datas?.data || []).map((row: any) => (
                                    <TableRow key={row.orderId} hover>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isRowSelected(row)}
                                                onChange={() => handleSelectRow(row)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={row.shipmentNumber}>
                                                <span>{row.shipmentNumber}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={row.orderNumber}>
                                                <span>{row.orderNumber}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={TimeHelper.formatDateHour(row.shipmentDate)}>
                                                <span>{TimeHelper.formatDateHour(row.shipmentDate)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{row.grandTotal}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            ) : isLoading ? (
                <SCUILoading />
            ) : (
                !isLoading && show === 1 && <NoResultFoundComponent errorText={t('general.noDataFound')} />
            )}
        </SCUIContent>
    );
};
